/**
 * shippingFees.service.ts — Admin > Shipping Fees: the account's flat
 * shipping fees, edited through /v5/shipping-fees as the corporate dashboard
 * does. A delete sets the fee inactive and takes it off every product that
 * carried it; restore brings the fee back but does not re-attach it. Writes
 * need Admin Access. (The read-only picker list is payments.service's
 * listShippingFees.)
 */
import { apiClient } from '@/services/apiClient';
import type { ApiResponse } from '@/types/api.types';

export interface AdminShippingFee {
  shippingFeeID: number;
  name: string;
  amount: number;
  feeOrder: number;
  active: boolean;
}

/** Every fee, deleted ones too, in the API's order (fee order, then name). */
export function listAdminShippingFees(): Promise<AdminShippingFee[]> {
  return apiClient
    .get<ApiResponse<Record<string, unknown>[]>>('/shipping-fees', { params: { include_inactive: 1 } })
    .then((r) =>
      (Array.isArray(r.data?.data) ? r.data.data : []).map((row) => ({
        shippingFeeID: Number(row.shippingFeeID ?? 0),
        name: String(row.name ?? ''),
        amount: Number(row.amount ?? 0) || 0,
        feeOrder: Number(row.feeOrder ?? 0) || 0,
        active: row.active === true || row.active === 1 || row.active === '1',
      })),
    );
}

export function createShippingFee(data: { name: string; amount: number }): Promise<void> {
  return apiClient.post('/shipping-fees', data).then(() => undefined);
}

export function updateShippingFee(id: number, data: { name: string; amount: number }): Promise<void> {
  return apiClient.put(`/shipping-fees/${id}`, data).then(() => undefined);
}

export function deleteShippingFee(id: number): Promise<void> {
  return apiClient.delete(`/shipping-fees/${id}`).then(() => undefined);
}

export function restoreShippingFee(id: number): Promise<void> {
  return apiClient.post(`/shipping-fees/${id}/restore`).then(() => undefined);
}
